import { AccountCapabilities } from 'types/capabilities/capabilities'

const General = {
  COOKIE_NAME: process.env.COOKIE_NAME || 'cdCookie201805',
  IMPERSONATION_COOKIE_NAME: 'cdImpersonation',
  COOKIE_PERMANENT: 'cdPrevious',
  IGNORE_2FA_COOKIE_NAME: 'ignoreWithdraw2faWarning',
  ZENDESK_COOKIE_NAME: 'cdZendeskAuth',
}

export const Image = {
  GLOBAL_IMG_PATH: `${process.env.RELEASE_ID || ''}`,
}

export const CookieTime = {
  TEN_SECONDS: 10 / 86400,
  ONE_HOUR: 1 / 24,
  ONE_DAY: 1,
}

export const SalesForce = {
  PARDOT_DEVX_ENDPOINT: 'https://info.bvnk.com/l/1013952/2023-02-23/bmr',
}

export const BVNK_BLOG_FRAUD_PREVENTION = 'https://www.bvnk.com/blog/app-fraud-businesses'

export default General

export const DateFormat = {
  DD_MMM_YYYY: 'DD MMM YYYY',
  DD_MMMM_YYYY: 'DD MMMM YYYY',
  DD_MM_YYYY: 'DD MM YYYY',
  DD_COMMA_MM_COMMA_YYYY: 'DD,MM,YYYY',
  D_MMM_YYYY_AT_HH_MM: 'D MMM YYYY @ HH:mm',
  DD_MMM_YYYY_HH_MM_A: 'DD MMM YYYY, hh:mm A',
  YYYY_MM_DD_HH_MM_SS: 'YYYY-MM-DD HH:mm:ss',
  YYYY_MM_DD: 'YYYY-MM-DD',
  DD_MMMM_YYYY_AT_HH_MM: 'DD MMMM YYYY @ HH:mm',
  HH_MM_DD_MMM_YYYY: 'HH:mm - DD MMM YYYY',
  LL: 'LL',
  H_MM_SS_A: 'h:mm:ss A',
  DD_MM_YYYY_: 'DD-MM-YYYY',
  MMM_D: 'MMM Do',
  MMM_YYYY: 'MMM YYYY',
}

export const PathNames = {
  AGREEMENTS: '/agreements',
  APPLICATION_ONBOARDING: '/application-onboarding',
  APPLICATION_UNSUCCESSFUL: '/application-unsuccessful',
  APPROVALS: '/approvals',
  AUTHENTICATE: '/authenticate',
  CONFIRM_ACCOUNT: '/create-account/1',
  CRYPTO_CONTACTS: '/crypto-contacts',
  CRYPTO_CONTACT: '/crypto-contacts/:id',
  CONTACTS: '/contacts',
  CONTACT: '/contacts/:id',
  CUSTOMERS: '/customers',
  CUSTOMER: '/customers/:id',
  CUSTOMERS_IN_FLIGHT: '/in-flight-customers',
  LIVE_CUSTOMERS: '/live-customers',
  CUSTOMER_WALLET: '/customers/:customerReference/:customerWalletId',
  CUSTOMER_FEE_SETUP: '/customers/fee-setup',
  DASHBOARD: '/',
  DEV_GET_STARTED: '/dev-get-started',
  EMBEDDED_CUSTOMERS: '/embedded-customers',
  FORGOT: '/forgot',
  FORGOT_PASSWORD: '/forgot-password',
  GET_STARTED: '/get-started',
  INTEGRATIONS: '/integrations',
  IMPERSONATE: '/impersonate',
  LEGAL_AGREEMENTS: '/manage-legal-agreements',
  LOGIN: '/login',
  LOGOUT: '/logout',
  MASS_PAYOUTS: '/mass-payouts',
  MASS_PAYOUTS_REVIEW: '/payout-details/:reference',
  MASS_PAYOUTS_LIST: '/list',
  MASS_CUSTOMER_LIMITS: '/mass-customer-limits',
  COMMUNICATION_PREFERENCES: '/communication-preferences',
  BILLING: '/billing',
  MANAGE_ACCOUNT: '/manage-account',
  MANAGE_BANK_ACCOUNTS: '/manage-bank-accounts',
  MANAGE_CHANNELS: '/manage-channels',
  MANAGE_MERCHANT_DETAILS: '/manage-merchant-details',
  MANAGE_PAYMENT_SUPPORT: '/manage-payment-support',
  PAYMENT_LIMITS: '/payment-limits',
  MANAGE_TEAM_MEMBERS: '/manage-team-members',
  MANAGE_USER_PROFILE: '/manage-user-profile',
  MISSING_CONTACTS: '/missing-contacts',
  MISSING_INFORMATION: '/missing-information',
  NOT_FOUND: '/404',
  EARN: '/earn',
  EARN_DETAILS: '/earn/:portfolioId',
  PAYMENTS: '/payments',
  PAYMENTS_CREATE: '/create',
  RESET_TOKEN: '/forgot/:token',
  REWARDS: '/rewards',
  REWARDS_DETAILS: '/rewards/:currency',
  SETTINGS: '/settings',
  TRANSACTION_HISTORY: '/transaction-history',
  UPGRADE: '/upgrade',
  VERIFY: '/verify',
  VERIFICATION: '/verification',
  WALLET_CREATE: '/create',
  WALLET_DASHBOARD: '/dashboard',
  WALLETS: '/wallets',
  WALLETS_CATCH_ALL: '/wallets/:code([w]{2,5})/:id([d]+)',
  WALLETS_DETAILS: '/wallet-details/:id',
  VERIFICATION_STATUS: '/verification-result/:status',
  ACCOUNT_ERROR: '/account-error',
  CRYPTO: '/crypto',
  CHANNEL: '/channel',
}

export type PathNamesType = (typeof PathNames)[keyof typeof PathNames]

export const DefaultLoginRedirect = PathNames.DASHBOARD

export const RedirectPathStateKey = 'redirectPath'

export const Approval = {
  ACCOUNT: 'Account',
  FLOWS: {
    ALL_PAYOUT: 'ALL_PAYOUT',
    FIAT_1ST_PARTY_PAYOUT: 'FIAT_1ST_PARTY_PAYOUT',
    FIAT_3RD_PARTY_PAYOUT: 'FIAT_3RD_PARTY_PAYOUT',
    CRYPTO_SEND: 'CRYPTO_SEND',
    CRYPTO_PAYOUT: 'CRYPTO_PAYOUT',
  },
  ACCOUNT_TRANSACTION_APPROVER: 'ACCOUNT_TRANSACTION_APPROVER',
  DECISION: {
    APPROVE: 'APPROVE',
    REJECT: 'REJECT',
  },
  MINIMUM_APPROVERS_FOR_MODAL_SEARCH: 5,
  RULES: {
    INFINITY: 'Infinity/Max',
  },
} as const

export const MERCHANT_PORTAL = 'merchantPortal'

export const DEFAULT_NUMBER_OF_ROWS = '20'

export const WALLETS_NUMBER_OF_ROWS = 20

export const DurationInMilliseconds = {
  TEN_SECONDS: 10000,
  TWENTY_SECONDS: 20000,
  THIRTY_SECONDS: 30000,
} as const

export const TWO_FACTOR_CODE_LENGTH = 6

export const DEFAULT_ACCOUNT_CAPABILITIES: AccountCapabilities = {
  isPrincipleCustomerCompanyCapabilityEnabled: false,
  isPrincipleCustomerIndividualCapabilityEnabled: false,
  isCubixBeneficiaryCapabilityEnabled: false,
  isEmbeddedCustomerCompanyCapabilityEnabled: false,
  isEmbeddedCustomerIndividualCapabilityEnabled: false,
  isAedBeneficiaryEnabled: false,
  isUsdCRNowBeneficiaryCapabilityEnabled: false,
  isUsdRtpBeneficiaryCapabilityEnabled: false,
  isUsdFedNowBeneficiaryCapabilityEnabled: false,
  isEpcWalletPayinsCapabilityEnabled: false,
  isEpcWalletPayoutsCapabilityEnabled: false,
  isEpcWalletInternalTransfersCapabilityEnabled: false,
  isEpcEmbeddedCustomerWalletCreationCapabilityEnabled: false,
  isPartnerManagedCustomerCompanyCapabilityEnabled: false,
  isPartnerManagedCustomerIndividualCapabilityEnabled: false,
  isEarningRewardsCapabilityEnabled: false,
  paxosUsStatesCapability: [],
  isLatamBankAccountCapabilityEnabled: false,
}

export const FINGERPRINT = {
  CACHE_TIME: 86400,
} as const

export const SEARCH_INPUT_DEBOUNCE_TIME = 500

export const PaymentSearchType = {
  REFERENCE: 'reference',
  HASH: 'hash',
  UUID: 'uuid',
} as const

export const ERROR_STATUS_CODES: Record<string, number> = {
  AUTH_REQUIRED: 428,
  FORBIDDEN: 403,
  BAD_REQUEST: 400,
  NOT_FOUND: 404,
}

export const TAG_ENABLED_NETWORKS = {
  XRP: 'XRP',
  XLM: 'XLM',
  STELLAR: 'STELLAR',
} as const
